import { useCallback, useEffect, useMemo, useState } from "react"
import Api from "@ciq/shared/api/api"
import { ZoneType } from "@ciq/shared/utils/const"
import { ErrorToast } from "@ciq/shared/utils/toast"
import {
  filterBuildingsByFloorStatus,
  getBuildingFloorStatusCounts,
  getFloorPlacementStatus,
} from "../helpers"

const INBOX_ITEM = { id: "inbox", label: "Inbox", type: "inbox" }

/**
 * Controller hook for the file upload sidebar.
 *
 * @param {Object} params
 * @param {Array} params.files
 * @param {string|null} params.schoolId
 * @returns {{
 *   sidebarItems: Array,
 *   selectedItemId: string,
 *   onSelectItem: function(string): void,
 *   activeFilter: string,
 *   onFilterChange: function(string): void,
 *   filterCounts: {all: number, none: number, "not-placed": number, placed: number},
 *   isLoading: boolean,
 *   contentHeader: string,
 *   expandedBuildings: Array<string>,
 *   onToggleBuilding: function(string): void,
 * }}
 */
export function useFileUploadSidebar({ files, schoolId }) {
  const [zones, setZones] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [activeFilter, setActiveFilter] = useState("all")
  const [selectedItemId, setSelectedItemId] = useState(INBOX_ITEM.id)
  const [expandedBuildings, setExpandedBuildings] = useState([])

  useEffect(() => {
    setSelectedItemId(INBOX_ITEM.id)
    setActiveFilter("all")
    setZones([])
    setIsLoading(false)
    setExpandedBuildings([])
  }, [schoolId])

  useEffect(() => {
    if (schoolId == null) return

    const controller = new AbortController()
    setIsLoading(true)

    Api.get(`schools/${schoolId}/wireless/pages`, { signal: controller.signal })
      .then(pagesResponse => {
        const pageCount = pagesResponse.data.pages.zones
        const pagePromises = []
        for (let page = 1; page <= pageCount; page++) {
          pagePromises.push(
            Api.get(`schools/${schoolId}/wireless/zones?page=${page}`, { signal: controller.signal })
          )
        }
        return Promise.all(pagePromises)
      })
      .then(responses => {
        const allZones = responses.flatMap(r => r.data.zones || [])
        setZones(allZones)
        setIsLoading(false)
      })
      .catch(e => {
        if (controller.signal.aborted) return

        setIsLoading(false)
        if (e.handled) return

        console.error(e.error)
        ErrorToast("Unable to load building data")
      })

    return () => {
      controller.abort()
    }
  }, [schoolId])

  const buildings = useMemo(
    () => zones
      .filter(zone => zone.type === ZoneType.BUILDING)
      .sort((a, b) => a.name.localeCompare(b.name)),
    [zones]
  )

  const floors = useMemo(
    () => zones.filter(zone => zone.type === ZoneType.FLOOR),
    [zones]
  )

  const allSidebarItems = useMemo(() => {
    const buildingItems = buildings.map(building => {
      const buildingFloors = floors
        .filter(floor => floor.parent?.id === building.id)
        .sort((a, b) => a.name.localeCompare(b.name))

      const buildingFiles = (files || []).filter(file => file.building_zone_id === building.id)

      return {
        id: building.id,
        label: building.name,
        type: "building",
        iconColor: building.layout?.icon_color,
        fileCount: buildingFiles.length,
        floors: buildingFloors.map(floor => ({
          id: floor.id,
          label: floor.name,
          type: "floor",
          iconColor: floor.layout?.icon_color,
          placementStatus: getFloorPlacementStatus(floor.id, files),
        })),
      }
    })

    return [INBOX_ITEM, ...buildingItems]
  }, [buildings, floors, files])

  const filterCounts = useMemo(
    () => getBuildingFloorStatusCounts(allSidebarItems.filter(item => item.type === "building")),
    [allSidebarItems]
  )

  const sidebarItems = useMemo(
    () => filterBuildingsByFloorStatus(allSidebarItems, activeFilter),
    [allSidebarItems, activeFilter]
  )

  const contentHeader = useMemo(() => {
    if (selectedItemId === INBOX_ITEM.id) return INBOX_ITEM.label

    const building = buildings.find(item => item.id === selectedItemId)
    if (building) return building.name

    const floor = floors.find(item => item.id === selectedItemId)
    if (floor) return floor.name

    return ""
  }, [selectedItemId, buildings, floors])

  useEffect(() => {
    if (selectedItemId === INBOX_ITEM.id) return

    const visibleIds = sidebarItems.flatMap(item => {
      if (item.type === "building") {
        return [item.id, ...(item.floors || []).map(floor => floor.id)]
      }
      return [item.id]
    })

    if (!visibleIds.includes(selectedItemId)) {
      setSelectedItemId(INBOX_ITEM.id)
    }
  }, [sidebarItems, selectedItemId])

  const onToggleBuilding = useCallback(id => {
    setExpandedBuildings(previous =>
      previous.includes(id) ? previous.filter(buildingId => buildingId !== id) : [...previous, id]
    )
  }, [])

  return {
    sidebarItems,
    allSidebarItems,
    selectedItemId,
    onSelectItem: setSelectedItemId,
    activeFilter,
    onFilterChange: setActiveFilter,
    filterCounts,
    isLoading,
    contentHeader,
    expandedBuildings,
    onToggleBuilding,
  }
}
